// AdminLayout — sidebar fija desktop-first para panel admin.
// Clona el patrón visual de ClienteLayout pero adaptado a la densidad del admin
// (más items, grupos colapsables). Mobile: drawer overlay.

import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { useAdminPresence, pathToLabel } from '../lib/useAdminPresence'

const MENU = [
  { type: 'link',  to: '/admin',                end: true,  label: 'Dashboard',       icon: '📊' },
  { type: 'link',  to: '/admin/marketing-agent',            label: 'Agente Diseño',   icon: '🤖' },
  { type: 'group', label: 'Clientes',           icon: '👥', items: [
    { to: '/admin/usuarios',                   label: 'Usuarios (todos)',     icon: '👤' },
    { to: '/admin/clientes',                   label: 'Suscriptores',         icon: '🎁' },
    { to: '/admin/pedidos',                    label: 'Pedidos one-shot',     icon: '🛍️' },
    { to: '/admin/postulaciones-porteros',     label: 'Postulaciones',        icon: '🛎️' },
  ]},
  { type: 'group', label: 'Logística',          icon: '🚚', items: [
    { to: '/admin/entregas',                   label: 'Entregas',             icon: '📦' },
    { to: '/admin/recordatorios',              label: 'Recordatorios WA',     icon: '📲' },
    { to: '/admin/barrios',                    label: 'Barrios',              icon: '🗺️' },
  ]},
  { type: 'group', label: 'Catálogo & Stock',   icon: '📦', items: [
    { to: '/admin/stock',                      label: 'Stock productos',      icon: '📊' },
    { to: '/admin/kits',                       label: 'Kits',                 icon: '🎁' },
    { to: '/admin/materiales',                 label: 'Stock materiales',     icon: '🧰' },
    { to: '/admin/proveedores',                label: 'Proveedores',          icon: '🏭' },
    { to: '/admin/orden-compra',               label: 'Órdenes de compra',    icon: '📋' },
  ]},
  { type: 'group', label: 'Administración',     icon: '💼', items: [
    { to: '/admin/compras',                    label: 'Compras (e-Facturas)', icon: '🧾' },
    { to: '/admin/cobros',                     label: 'Cobros',               icon: '💰' },
    { to: '/admin/reportes/iva',               label: 'IVA mensual (DGI)',    icon: '🧮' },
    { to: '/admin/reportes/comisiones-mp',     label: 'Comisiones MP',        icon: '💳' },
  ]},
  { type: 'group', label: 'Reportes',           icon: '📈', items: [
    { to: '/admin/reportes',                   label: 'General (P&L)',        icon: '📊' },
    { to: '/admin/reportes/modelo-financiero', label: 'Modelo Financiero',    icon: '🧮' },
    { to: '/admin/reportes/mrr-churn',         label: 'MRR + Churn',          icon: '📈' },
    { to: '/admin/reportes/cohort-ltv',        label: 'Cohort + LTV',         icon: '👥' },
    { to: '/admin/reportes/vecinos',           label: 'Vecinos / Referidos',  icon: '🏢' },
    { to: '/admin/reportes/rentabilidad-producto', label: 'Rent. por producto', icon: '🏆' },
    { to: '/admin/reportes/rentabilidad-barrio',   label: 'Rent. por barrio',   icon: '🗺️' },
    { to: '/admin/reportes/stock-turnover',    label: 'Stock turnover',       icon: '🔄' },
  ]},
  { type: 'group', label: 'Marketing',          icon: '📣', items: [
    { to: '/admin/calendario-marketing',       label: 'Calendario editorial', icon: '🗓️' },
    { to: '/admin/marketing/volante',          label: 'Volante A4',           icon: '📄' },
  ]},
  { type: 'link',  to: '/repartidor/hoy',                   label: 'Mi ruta',         icon: '📱' },
  { type: 'link',  to: '/admin/configuracion-ia',           label: 'IA',              icon: '🤖' },
]

export default function AdminLayout({ children, title }) {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Recordar grupos abiertos en localStorage
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_open_groups')
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    // Default: abre el grupo que contiene la página activa
    const init = {}
    MENU.forEach(g => {
      if (g.type === 'group' && g.items.some(i => path.startsWith(i.to))) init[g.label] = true
    })
    return init
  })

  useEffect(() => {
    // Abrir el grupo al navegar a una sección dentro
    setOpenGroups(prev => {
      const next = { ...prev }
      MENU.forEach(g => {
        if (g.type === 'group' && g.items.some(i => path.startsWith(i.to))) next[g.label] = true
      })
      return next
    })
    setDrawerOpen(false)
  }, [path])

  useEffect(() => {
    try { localStorage.setItem('admin_open_groups', JSON.stringify(openGroups)) } catch (_) {}
  }, [openGroups])

  function toggleGroup(label) {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }))
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  function isLinkActive(to, end) {
    if (end) return path === to
    return path === to || path.startsWith(to + '/')
  }

  function isGroupActive(items) {
    return items.some(i => isLinkActive(i.to, false))
  }

  const initial = (profile?.nombre || user?.email || 'U').charAt(0).toUpperCase()
  const displayName = profile?.nombre?.split(' ')[0] || (user?.email || '').split('@')[0]
  const fullEmail = user?.email || ''

  // Presencia: lista de admins online (incluye self)
  const adminsOnline = useAdminPresence()
  const otrosAdmins = adminsOnline.filter(a => a.user_id !== user?.id)

  function NavBody() {
    return (
      <>
        <Link to="/admin" className="al-logo">Lim<span>pack</span><em>admin</em></Link>

        {/* Indicador "conectado como" */}
        <div className="al-me">
          <span className="al-me-dot"></span>
          <span className="al-me-text">Conectado como <strong>{displayName}</strong></span>
        </div>

        <nav className="al-nav">
          {MENU.map((m, idx) => {
            if (m.type === 'link') {
              const active = isLinkActive(m.to, m.end)
              return (
                <Link key={m.to} to={m.to} className={`al-link ${active ? 'active' : ''}`}>
                  <span className="al-icon">{m.icon}</span>
                  <span>{m.label}</span>
                </Link>
              )
            }
            const open = !!openGroups[m.label]
            const active = isGroupActive(m.items)
            return (
              <div key={m.label} className={`al-group ${open ? 'open' : ''} ${active ? 'has-active' : ''}`}>
                <button type="button" className="al-group-head" onClick={() => toggleGroup(m.label)}>
                  <span className="al-icon">{m.icon}</span>
                  <span className="al-group-label">{m.label}</span>
                  <span className="al-chev">{open ? '▾' : '▸'}</span>
                </button>
                {open && (
                  <div className="al-group-items">
                    {m.items.map(i => {
                      const a = isLinkActive(i.to, false)
                      return (
                        <Link key={i.to} to={i.to} className={`al-sublink ${a ? 'active' : ''}`}>
                          <span className="al-sub-icon">{i.icon}</span>
                          <span>{i.label}</span>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* Otros admins online y donde estan */}
        {otrosAdmins.length > 0 && (
          <div className="al-presence">
            <div className="al-presence-tag">Otros admins online</div>
            {otrosAdmins.map(a => (
              <div key={a.user_id} className="al-presence-row" title={a.email}>
                <span className="al-presence-dot"></span>
                <span className="al-presence-name">{a.nombre}</span>
                <span className="al-presence-where">{pathToLabel(a.path, MENU)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="al-footer">
          <Link to="/admin/configuracion-ia" className="al-user">
            <div className="al-avatar">{initial}</div>
            <div className="al-user-info">
              <div className="al-user-name">{displayName}</div>
              <div className="al-user-email">{fullEmail}</div>
            </div>
          </Link>
          <button className="al-logout" onClick={handleLogout}>Salir</button>
        </div>
      </>
    )
  }

  return (
    <div className="al-shell">
      {/* Sidebar desktop */}
      <aside className="al-side">
        <NavBody />
      </aside>

      {/* Drawer mobile */}
      {drawerOpen && (
        <>
          <div className="al-overlay" onClick={() => setDrawerOpen(false)} />
          <aside className="al-side al-side-mobile">
            <NavBody />
          </aside>
        </>
      )}

      {/* Header mobile */}
      <header className="al-header-mobile">
        <button className="al-burger" onClick={() => setDrawerOpen(true)} aria-label="Menú">☰</button>
        <Link to="/admin" className="al-logo-mobile">Lim<span>pack</span><em>admin</em></Link>
        <button className="al-icon-btn" onClick={() => navigate('/admin/configuracion-ia')} aria-label="Cuenta">{initial}</button>
      </header>

      {/* Main */}
      <main className="al-main">
        {title && <div className="al-page-title">{title}</div>}
        {children}
      </main>

      <style>{`
        :root {
          --al-navy: #0F1F3D; --al-navy-2: #1a2a4f;
          --al-blue: #1a5cb8; --al-teal: #00C4D4;
          --al-bg: #f5f7fb; --al-card: #fff;
          --al-line: #e2e8f0; --al-line2: #f1f5f9;
          --al-muted: #64748b; --al-muted2: #94a3b8;
        }
        .al-shell {
          min-height: 100vh; background: var(--al-bg);
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif;
          color: var(--al-navy);
        }

        /* Logos */
        .al-logo, .al-logo-mobile {
          font-weight: 900; font-size: 22px; letter-spacing: -.02em;
          color: #fff; text-decoration: none;
          display: flex; align-items: baseline; gap: 6px;
          padding: 0 8px 18px;
        }
        .al-logo span, .al-logo-mobile span { color: var(--al-teal) }

        /* Indicador "conectado como" */
        .al-me {
          display: flex; align-items: center; gap: 8px;
          margin: -8px 8px 14px; padding: 8px 12px;
          background: rgba(0,196,212,.08); border: 1px solid rgba(0,196,212,.2);
          border-radius: 8px; color: rgba(255,255,255,.85); font-size: 12px;
        }
        .al-me-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #22C55E; box-shadow: 0 0 0 3px rgba(34,197,94,.25);
          flex-shrink: 0;
        }
        .al-me-text strong { font-weight: 700; color: #fff }

        /* Otros admins online */
        .al-presence {
          margin: 14px 8px 6px; padding: 10px 12px;
          background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.08);
          border-radius: 8px;
        }
        .al-presence-tag {
          font-size: 9px; font-weight: 800; letter-spacing: .14em;
          color: rgba(255,255,255,.5); text-transform: uppercase;
          margin-bottom: 8px;
        }
        .al-presence-row {
          display: flex; align-items: center; gap: 8px;
          font-size: 12px; color: rgba(255,255,255,.85);
          padding: 4px 0;
        }
        .al-presence-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #22C55E; flex-shrink: 0;
        }
        .al-presence-name { font-weight: 700; color: #fff }
        .al-presence-where {
          font-size: 11px; color: rgba(255,255,255,.55);
          margin-left: auto; max-width: 110px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .al-logo em, .al-logo-mobile em {
          font-size: 10px; font-weight: 700; letter-spacing: .14em;
          color: rgba(255,255,255,.5); text-transform: uppercase;
          font-style: normal;
        }

        /* Sidebar (desktop) */
        .al-side {
          display: none;
          position: fixed; top: 0; left: 0; bottom: 0; width: 252px;
          background: var(--al-navy); color: #fff;
          padding: 22px 12px 16px; flex-direction: column; z-index: 50;
          overflow-y: auto;
        }
        .al-side::-webkit-scrollbar { width: 6px }
        .al-side::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 3px }
        .al-nav { display: flex; flex-direction: column; gap: 1px; flex: 1 }

        /* Top-level link */
        .al-link {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: 9px;
          color: rgba(255,255,255,.75);
          font-size: 13.5px; font-weight: 600;
          text-decoration: none;
          transition: background .12s, color .12s;
        }
        .al-link:hover { background: rgba(255,255,255,.06); color: #fff }
        .al-link.active { background: rgba(0,196,212,.18); color: var(--al-teal) }
        .al-icon { font-size: 16px; width: 20px; text-align: center; flex-shrink: 0 }

        /* Group */
        .al-group { display: flex; flex-direction: column }
        .al-group-head {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: 9px;
          background: none; border: 0; cursor: pointer;
          color: rgba(255,255,255,.65);
          font-size: 12.5px; font-weight: 700;
          text-transform: uppercase; letter-spacing: .04em;
          font-family: inherit; text-align: left;
          transition: color .12s, background .12s;
        }
        .al-group-head:hover { color: #fff; background: rgba(255,255,255,.04) }
        .al-group.has-active .al-group-head { color: rgba(255,255,255,.9) }
        .al-group-label { flex: 1 }
        .al-chev { font-size: 10px; opacity: .6 }
        .al-group-items {
          display: flex; flex-direction: column; gap: 1px;
          padding: 2px 0 6px 8px;
          margin-left: 12px;
          border-left: 1px solid rgba(255,255,255,.08);
        }
        .al-sublink {
          display: flex; align-items: center; gap: 8px;
          padding: 7px 10px; border-radius: 7px;
          color: rgba(255,255,255,.6);
          font-size: 12.5px; font-weight: 500;
          text-decoration: none;
          transition: background .12s, color .12s;
        }
        .al-sublink:hover { background: rgba(255,255,255,.05); color: #fff }
        .al-sublink.active { background: rgba(0,196,212,.16); color: var(--al-teal); font-weight: 700 }
        .al-sub-icon { font-size: 13px; width: 16px; text-align: center; flex-shrink: 0 }

        /* Footer */
        .al-footer {
          display: flex; flex-direction: column; gap: 6px;
          margin-top: 12px; padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,.08);
        }
        .al-user {
          display: flex; align-items: center; gap: 10px; padding: 8px;
          border-radius: 9px; background: rgba(255,255,255,.05);
          text-decoration: none;
        }
        .al-user:hover { background: rgba(255,255,255,.08) }
        .al-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: linear-gradient(135deg, var(--al-teal), var(--al-blue));
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; color: #fff; font-size: 13px; flex-shrink: 0;
        }
        .al-user-info { flex: 1; min-width: 0 }
        .al-user-name { font-size: 12px; font-weight: 700; color: #fff }
        .al-user-email { font-size: 10px; color: rgba(255,255,255,.5); white-space: nowrap; overflow: hidden; text-overflow: ellipsis }
        .al-logout {
          background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
          color: rgba(255,255,255,.85); border-radius: 8px;
          padding: 7px; font-size: 11.5px; font-weight: 700;
          cursor: pointer; font-family: inherit;
        }
        .al-logout:hover { background: rgba(255,255,255,.1) }

        /* Mobile drawer + overlay */
        .al-overlay {
          position: fixed; inset: 0; background: rgba(15,31,61,.4);
          z-index: 49; backdrop-filter: blur(2px);
        }
        .al-side-mobile { display: flex; left: 0 }

        /* Mobile header */
        .al-header-mobile {
          position: sticky; top: 0; z-index: 40;
          background: var(--al-card); border-bottom: 1px solid var(--al-line);
          padding: 12px 14px;
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
        }
        .al-burger {
          background: var(--al-line2); border: 0; border-radius: 8px;
          width: 36px; height: 36px; cursor: pointer; font-size: 18px;
        }
        .al-logo-mobile { color: var(--al-navy); padding: 0; font-size: 16px }
        .al-logo-mobile em { color: var(--al-muted2) }
        .al-icon-btn {
          width: 34px; height: 34px; border-radius: 50%;
          background: linear-gradient(135deg, var(--al-teal), var(--al-blue));
          color: #fff; border: 0; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800;
        }

        /* Main */
        .al-main {
          padding: 18px 18px 60px;
          min-height: calc(100vh - 60px);
        }
        .al-page-title {
          font-size: 22px; font-weight: 800; letter-spacing: -.02em;
          margin-bottom: 14px; color: var(--al-navy);
        }

        /* Desktop breakpoint */
        @media (min-width: 960px) {
          .al-side { display: flex }
          .al-header-mobile { display: none }
          .al-main { margin-left: 252px; padding: 28px 32px 60px }
          .al-page-title { font-size: 26px; margin-bottom: 18px }
        }

        /* Compatibilidad con clases legacy de Layout viejo */
        .al-main .page { padding: 0 }
        .al-main .page > h1:first-child { margin-top: 0 }
        .al-main .lead { color: var(--al-muted); margin-top: -6px }
      `}</style>
    </div>
  )
}
